import * as React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    Button,
    makeStyles,
    Menu,
    MenuDivider,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Persona,
    Text,
    tokens,
} from '@fluentui/react-components';
import {
    Add20Regular,
    ChevronUpDown20Regular,
    Flash20Regular,
    Library20Filled,
    Library20Regular,
    Poll20Filled,
    Poll20Regular,
    SignOut20Regular,
    TaskListSquareLtr20Regular,
} from '@fluentui/react-icons';
import { useAuth } from '../auth/AuthContext';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
    },
    brand: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        paddingTop: '18px',
        paddingBottom: '18px',
        paddingLeft: '20px',
        paddingRight: '20px',
        textDecoration: 'none',
        color: 'inherit',
    },
    brandMark: {
        width: '36px',
        height: '36px',
        borderRadius: tokens.borderRadiusCircular,
        flexShrink: 0,
        boxShadow: `0 2px 8px ${tokens.colorBrandShadowAmbient}`,
    },
    brandText: {
        display: 'flex',
        flexDirection: 'column',
        lineHeight: 1.15,
        minWidth: 0,
    },
    brandName: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '-0.01em',
    },
    brandKicker: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    create: {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingBottom: '8px',
    },
    createButton: {
        width: '100%',
        justifyContent: 'flex-start',
        minHeight: '40px',
    },
    nav: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        paddingLeft: '12px',
        paddingRight: '12px',
        paddingTop: '8px',
        flexGrow: 1,
        overflowY: 'auto',
    },
    navLabel: {
        paddingLeft: '12px',
        paddingTop: '8px',
        paddingBottom: '6px',
        fontSize: tokens.fontSizeBase100,
        fontWeight: tokens.fontWeightSemibold,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: tokens.colorNeutralForeground4,
    },
    link: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minHeight: '40px',
        paddingLeft: '12px',
        paddingRight: '12px',
        borderRadius: tokens.borderRadiusMedium,
        color: tokens.colorNeutralForeground2,
        textDecoration: 'none',
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightMedium,
        transitionProperty: 'background-color, color',
        transitionDuration: tokens.durationFaster,
        transitionTimingFunction: tokens.curveEasyEase,
        ':hover': {
            backgroundColor: tokens.colorSubtleBackgroundHover,
            color: tokens.colorNeutralForeground1,
        },
        ':active': {
            backgroundColor: tokens.colorSubtleBackgroundPressed,
        },
        ':focus-visible': {
            outline: `2px solid ${tokens.colorStrokeFocus2}`,
            outlineOffset: '1px',
        },
    },
    linkActive: {
        color: tokens.colorBrandForeground1,
        backgroundColor: tokens.colorBrandBackground2,
        fontWeight: tokens.fontWeightSemibold,
        '::before': {
            content: '""',
            position: 'absolute',
            left: '-12px',
            top: '8px',
            bottom: '8px',
            width: '3px',
            borderTopRightRadius: '3px',
            borderBottomRightRadius: '3px',
            backgroundColor: tokens.colorBrandBackground,
        },
        ':hover': {
            backgroundColor: tokens.colorBrandBackground2Hover,
            color: tokens.colorBrandForeground1,
        },
    },
    linkIcon: {
        display: 'inline-flex',
        flexShrink: 0,
    },
    footer: {
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        paddingTop: '10px',
        paddingBottom: '12px',
        paddingLeft: '12px',
        paddingRight: '12px',
    },
    account: {
        width: '100%',
        justifyContent: 'space-between',
        minHeight: '48px',
        paddingLeft: '8px',
        paddingRight: '8px',
    },
    accountPersona: {
        minWidth: 0,
        '& span': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
});

interface INavItem {
    to: string;
    label: string;
    icon: React.ReactElement;
    activeIcon: React.ReactElement;
}

const NAV_ITEMS: INavItem[] = [
    { to: '/polls', label: 'Опросы', icon: <Poll20Regular />, activeIcon: <Poll20Filled /> },
    { to: '/templates', label: 'Шаблоны вопросов', icon: <Library20Regular />, activeIcon: <Library20Filled /> },
];

export interface ISidebarNavProps {
    /** Called after any navigation, so the mobile drawer can close itself. */
    onNavigate?: () => void;
}

/** Shared between the desktop rail and the mobile drawer. */
const SidebarNav: React.FC<ISidebarNavProps> = ({ onNavigate }) => {
    const styles = useStyles();
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    const go = (to: string): void => {
        navigate(to);
        onNavigate?.();
    };

    return (
        <div className={styles.root}>
            <NavLink to="/polls" className={styles.brand} onClick={onNavigate}>
                <img src="/favicon.svg" alt="" className={styles.brandMark} />
                <span className={styles.brandText}>
                    <Text className={styles.brandName}>Bayer Poll</Text>
                    <Text className={styles.brandKicker}>Управление опросами</Text>
                </span>
            </NavLink>

            <div className={styles.create}>
                <Menu positioning="below-start">
                    <MenuTrigger disableButtonEnhancement>
                        <Button appearance="primary" icon={<Add20Regular />} className={styles.createButton}>
                            Новый опрос
                        </Button>
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            <MenuItem icon={<Flash20Regular />} secondaryContent="1 вопрос" onClick={() => go('/polls/new/quick')}>
                                Быстрый опрос
                            </MenuItem>
                            <MenuItem icon={<TaskListSquareLtr20Regular />} secondaryContent="Несколько" onClick={() => go('/polls/new/complex')}>
                                Комплексный опрос
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            </div>

            <nav className={styles.nav} aria-label="Основная навигация">
                <Text className={styles.navLabel}>Разделы</Text>
                {NAV_ITEMS.map((item) => (
                    <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={onNavigate}
                        className={({ isActive }) => mergeClasses(styles.link, isActive && styles.linkActive)}
                    >
                        {({ isActive }) => (
                            <>
                                <span className={styles.linkIcon}>{isActive ? item.activeIcon : item.icon}</span>
                                {item.label}
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            {user && (
                <div className={styles.footer}>
                    <Menu positioning="above-start">
                        <MenuTrigger disableButtonEnhancement>
                            <Button appearance="subtle" className={styles.account} icon={<ChevronUpDown20Regular />} iconPosition="after">
                                <Persona
                                    className={styles.accountPersona}
                                    name={user.displayName}
                                    secondaryText={user.roles.length ? user.roles.join(', ') : user.username}
                                    size="small"
                                    avatar={{ color: 'colorful' }}
                                    textAlignment="center"
                                />
                            </Button>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem disabled>{user.username}</MenuItem>
                                <MenuDivider />
                                <MenuItem icon={<SignOut20Regular />} onClick={() => { void logout(); }}>
                                    Выйти
                                </MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                </div>
            )}
        </div>
    );
};

export default SidebarNav;
